import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Avatar, AvatarFallback } from './ui/avatar';
import { 
  MessageSquare, Send, ArrowLeft, User, Briefcase, 
  Check, CheckCheck, Clock, Loader2, X
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

// Chat List Component - Shows all conversations
export const ChatList = ({ onSelectConversation, selectedId }) => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadTotal, setUnreadTotal] = useState(0);

  useEffect(() => {
    fetchConversations();
    fetchUnreadCount();
    
    // Poll for new messages
    const interval = setInterval(() => {
      fetchConversations();
      fetchUnreadCount();
    }, 10000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchConversations = async () => {
    try {
      const response = await api.get('/conversations');
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Failed to fetch conversations');
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await api.get('/conversations/unread/count');
      setUnreadTotal(response.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch unread count');
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin" size={24} />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-testid="chat-list">
      <div className="p-4 border-b flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageSquare className="text-primary" size={24} />
          <h2 className="text-lg font-semibold">Messages</h2>
          {unreadTotal > 0 && (
            <Badge variant="destructive" className="rounded-full">
              {unreadTotal}
            </Badge>
          )}
        </div>
      </div>
      
      <ScrollArea className="flex-1">
        {conversations.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <MessageSquare size={48} className="mx-auto mb-4 opacity-50" />
            <p>No conversations yet</p>
          </div>
        ) : (
          <div className="divide-y">
            {conversations.map((conv) => (
              <div
                key={conv.id}
                onClick={() => onSelectConversation(conv)}
                className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                  selectedId === conv.id ? 'bg-primary/5 border-l-4 border-primary' : ''
                }`}
                data-testid={`conversation-${conv.id}`}
              >
                <div className="flex items-start gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {conv.customer_name?.charAt(0) || 'C'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="font-semibold truncate">{conv.customer_name}</p>
                      <span className="text-xs text-gray-400">
                        {formatTime(conv.last_message_at)}
                      </span>
                    </div>
                    {conv.job_reference && (
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <Briefcase size={12} />
                        <span>Job #{conv.job_reference}</span>
                      </div>
                    )}
                    <p className="text-sm text-gray-600 truncate mt-1">
                      {conv.last_message || 'No messages yet'}
                    </p>
                  </div>
                  {conv.unread_count > 0 && (
                    <Badge variant="destructive" className="rounded-full">
                      {conv.unread_count}
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
};

// Chat Window Component - Shows conversation messages
export const ChatWindow = ({ conversation, onBack, currentUser }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (conversation?.id) {
      fetchMessages();
      markAsRead();
      connectWebSocket();
    }
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [conversation?.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const connectWebSocket = () => {
    const token = localStorage.getItem('movemate_token');
    if (!token || !conversation?.id) return;
    
    const wsUrl = `${process.env.REACT_APP_BACKEND_URL?.replace('https', 'wss').replace('http', 'ws')}/ws/chat/${conversation.id}?token=${token}`;
    
    try {
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        console.log('WebSocket connected');
      };
      
      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'new_message') {
          setMessages(prev => [...prev, data.message]);
          markAsRead();
        }
      };
      
      ws.onclose = () => {
        console.log('WebSocket disconnected');
      };
      
      wsRef.current = ws;
    } catch (error) {
      console.error('WebSocket error:', error);
    }
  };

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/conversations/${conversation.id}/messages`);
      setMessages(response.data.messages || []);
    } catch (error) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async () => {
    try {
      await api.post(`/conversations/${conversation.id}/read`);
    } catch (error) {
      // Silent fail
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;
    
    setSending(true);
    try {
      const response = await api.post(`/conversations/${conversation.id}/messages`, {
        content: newMessage.trim()
      });
      
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatMessageTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const isOwnMessage = (message) => {
    return message.sender_id === currentUser?.id;
  };

  if (!conversation) return null;

  return (
    <div className="h-full flex flex-col" data-testid="chat-window">
      {/* Header */}
      <div className="p-4 border-b flex items-center gap-3 bg-white">
        <Button variant="ghost" size="icon" onClick={onBack} className="md:hidden">
          <ArrowLeft size={20} />
        </Button>
        <Avatar className="h-10 w-10">
          <AvatarFallback className="bg-primary/10 text-primary">
            {conversation.customer_name?.charAt(0) || 'C'}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <p className="font-semibold">{conversation.customer_name}</p>
          {conversation.job_reference && (
            <p className="text-xs text-gray-500">Job #{conversation.job_reference}</p>
          )}
        </div>
      </div>
      
      {/* Messages */}
      <ScrollArea className="flex-1 p-4 bg-gray-50">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="animate-spin" size={24} />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            <MessageSquare size={48} className="mx-auto mb-4 opacity-50" />
            <p>No messages yet. Start the conversation!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((msg, idx) => {
              const isOwn = isOwnMessage(msg);
              return (
                <div
                  key={msg.id || idx}
                  className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[75%] ${isOwn ? 'order-2' : 'order-1'}`}>
                    {!isOwn && (
                      <p className="text-xs text-gray-500 mb-1 ml-1">
                        {msg.sender_name}
                      </p>
                    )}
                    <div
                      className={`px-4 py-2 rounded-2xl ${
                        isOwn
                          ? 'bg-primary text-white rounded-br-md'
                          : 'bg-white border rounded-bl-md'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    </div>
                    <div className={`flex items-center gap-1 mt-1 text-xs text-gray-400 ${
                      isOwn ? 'justify-end' : 'justify-start'
                    }`}>
                      <span>{formatMessageTime(msg.created_at)}</span>
                      {isOwn && (
                        msg.status === 'read' ? (
                          <CheckCheck size={14} className="text-blue-500" />
                        ) : (
                          <Check size={14} />
                        )
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        )}
      </ScrollArea>
      
      {/* Message Input */}
      <form onSubmit={handleSend} className="p-4 border-t bg-white">
        <div className="flex gap-2">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            className="flex-1"
            disabled={sending}
            data-testid="message-input"
          />
          <Button type="submit" disabled={!newMessage.trim() || sending} data-testid="send-btn">
            {sending ? (
              <Loader2 className="animate-spin" size={18} />
            ) : (
              <Send size={18} />
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

// Full Chat Page Component
const ChatPage = ({ currentUser }) => {
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSelectConversation = (conv) => {
    setSelectedConversation(conv);
  };

  const handleBack = () => {
    setSelectedConversation(null);
  };

  // Mobile view
  if (isMobile) {
    return (
      <div className="h-[calc(100vh-120px)]">
        {selectedConversation ? (
          <ChatWindow
            conversation={selectedConversation}
            onBack={handleBack}
            currentUser={currentUser}
          />
        ) : (
          <ChatList
            onSelectConversation={handleSelectConversation}
            selectedId={null}
          />
        )}
      </div>
    );
  }

  // Desktop view
  return (
    <div className="h-[calc(100vh-120px)] flex border rounded-lg overflow-hidden bg-white">
      <div className="w-80 border-r">
        <ChatList
          onSelectConversation={handleSelectConversation}
          selectedId={selectedConversation?.id}
        />
      </div>
      <div className="flex-1">
        {selectedConversation ? (
          <ChatWindow
            conversation={selectedConversation}
            onBack={handleBack}
            currentUser={currentUser}
          />
        ) : (
          <div className="h-full flex items-center justify-center text-gray-500">
            <div className="text-center">
              <MessageSquare size={64} className="mx-auto mb-4 opacity-30" />
              <p>Select a conversation to start messaging</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatPage;
